import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { fetchPatientDirectory } from "../../../utils/api";
import { getErrorMessage } from "./emailFormatters";

const PAGE_SIZE = 50;

const normalizeDirectoryPage = (response) => {
  const payload = response?.data || response || {};
  return {
    content: Array.isArray(payload.content) ? payload.content : [],
    totalElements: Number(payload.totalElements || 0),
    totalPages: Number(payload.totalPages || 0),
  };
};

export const patientName = (patient) => {
  const firstName = patient?.firstName || "";
  const lastName = patient?.lastName || "";
  const name = `${firstName} ${lastName}`.trim();
  return name || "Unnamed participant";
};

export const patientIdOf = (patient) => patient?.id || patient?.patientId || "";

const patientSearchFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 36,
    borderRadius: "4px",
    fontSize: "0.875rem",
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
  },
};

const EmailPatientPicker = ({
  selectedPatientId = "",
  onSelectPatient,
  includeAll = false,
  allLabel = "All Patients",
  allDescription = "Show all email messages",
  includeUnmatched = false,
  includeContact = false,
  showContact = false,
  showPhone = true,
  selectedScope = "all",
  height = 520,
  compact = false,
  showHeader = true,
}) => {
  const [draft, setDraft] = useState({ name: "", patientAccountNumber: "" });
  const [applied, setApplied] = useState({ name: "", patientAccountNumber: "" });
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadPatients = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetchPatientDirectory({
        name: applied.name,
        patientAccountNumber: applied.patientAccountNumber,
        includeContact,
        page,
        size: PAGE_SIZE,
      });
      const directoryPage = normalizeDirectoryPage(response);
      setItems(directoryPage.content);
      setTotalElements(directoryPage.totalElements);
      setTotalPages(directoryPage.totalPages);
    } catch (err) {
      console.error("Failed to load patient directory:", err);
      setItems([]);
      setTotalElements(0);
      setTotalPages(0);
      setError(getErrorMessage(err, "Failed to load patient directory."));
    } finally {
      setLoading(false);
    }
  }, [applied, includeContact, page]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  const updateDraft = (field) => (event) => {
    setDraft((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSearch = () => {
    setApplied({
      name: draft.name.trim(),
      patientAccountNumber: draft.patientAccountNumber.trim(),
    });
    setPage(0);
  };

  const handleClear = () => {
    setDraft({ name: "", patientAccountNumber: "" });
    setApplied({ name: "", patientAccountNumber: "" });
    setPage(0);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const from = totalElements === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, totalElements);
  const canPrevious = page > 0 && !loading;
  const canNext = page + 1 < totalPages && !loading;

  return (
    <Box sx={{ height, display: "flex", flexDirection: "column", bgcolor: "#fff" }}>
      <Box sx={{ px: compact ? 1.5 : 2, py: compact ? 1 : 1.5, borderBottom: "1px solid #d0d0d0" }}>
        {showHeader && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Patients
            </Typography>
            {loading && <CircularProgress color="inherit" size={18} />}
          </Stack>
        )}
        <Stack
          direction={compact ? "row" : "column"}
          spacing={1}
          alignItems={compact ? "center" : "stretch"}
          sx={{ mt: showHeader ? 1.5 : 0 }}
          onKeyDown={handleKeyDown}
        >
          <TextField
            placeholder="Name contains"
            size="small"
            value={draft.name}
            onChange={updateDraft("name")}
            sx={{ ...patientSearchFieldSx, flex: compact ? "1 1 0" : undefined, minWidth: compact ? 0 : undefined }}
            fullWidth
          />
          <TextField
            placeholder="MRN starts with"
            size="small"
            value={draft.patientAccountNumber}
            onChange={updateDraft("patientAccountNumber")}
            sx={{ ...patientSearchFieldSx, flex: compact ? "1 1 0" : undefined, minWidth: compact ? 0 : undefined }}
            fullWidth
          />
          <Stack direction="row" spacing={1} sx={{ width: compact ? "auto" : "100%", flexShrink: 0 }}>
            <Tooltip title="Clear patient search" arrow>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClear}
                aria-label="Clear patient search"
                sx={{
                  flex: compact ? "0 0 auto" : 1,
                  minWidth: 0,
                  width: compact ? 36 : undefined,
                  height: 36,
                  px: 0,
                }}
              >
                <ClearIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title="Search patients" arrow>
              <Button
                variant="contained"
                size="small"
                onClick={handleSearch}
                aria-label="Search patients"
                sx={{
                  flex: compact ? "0 0 auto" : 1,
                  minWidth: 0,
                  width: compact ? 36 : undefined,
                  height: 36,
                  px: 0,
                }}
              >
                <SearchIcon fontSize="small" />
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <List disablePadding sx={{ flex: 1, overflowY: "auto" }}>
        {compact && (
          <Stack
            direction="row"
            sx={{
              px: 2,
              py: 1,
              borderBottom: "1px solid #eeeeee",
              bgcolor: "rgba(0, 0, 0, 0.04)",
              alignItems: "center",
              gap: 1
            }}
          >
            <Typography variant="subtitle2" sx={{ flex: 6, textAlign: "center", fontWeight: "bold" }}>Name</Typography>
            <Typography variant="subtitle2" sx={{ flex: 4, textAlign: "center", fontWeight: "bold" }}>MRN</Typography>
            <Typography variant="subtitle2" sx={{ flex: 8, textAlign: "center", fontWeight: "bold" }}>Email</Typography>
            {showPhone && (
              <Typography variant="subtitle2" sx={{ flex: 5, textAlign: "center", fontWeight: "bold" }}>Phone</Typography>
            )}
          </Stack>
        )}
        {includeAll && (
          <ListItemButton
            selected={selectedScope === "all"}
            onClick={() => {
              handleClear();
              onSelectPatient?.(null, "all");
            }}
            sx={{
              borderBottom: "1px solid #eeeeee",
              "&.Mui-selected": { bgcolor: "rgba(80, 0, 0, 0.10)" },
              "&.Mui-selected:hover": { bgcolor: "rgba(80, 0, 0, 0.14)" },
            }}
          >
            <ListItemText
              primary={allLabel}
              secondary={allDescription}
              primaryTypographyProps={{ fontWeight: 700, variant: "body2" }}
              secondaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
        )}
        {includeUnmatched && (
          <ListItemButton
            selected={selectedScope === "unmatched"}
            onClick={() => onSelectPatient?.(null, "unmatched")}
            sx={{
              borderBottom: "1px solid #eeeeee",
              "&.Mui-selected": { bgcolor: "rgba(80, 0, 0, 0.10)" },
              "&.Mui-selected:hover": { bgcolor: "rgba(80, 0, 0, 0.14)" },
            }}
          >
            <ListItemText
              primary="Unmatched"
              secondary="Messages without a patient"
              primaryTypographyProps={{ fontWeight: 700, variant: "body2" }}
              secondaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
        )}
        {error && (
          <Box sx={{ p: 1.5 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        {!error && items.length === 0 && !loading && (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No patients found.
            </Typography>
          </Box>
        )}
        {!error && items.map((patient) => {
          const id = patientIdOf(patient);
          const selected = selectedScope === "patient" && selectedPatientId === id;
          const name = patientName(patient);
          const contactLines = [
            patient.patientAccountNumber || "No MRN",
            patient.email ? `Email: ${patient.email}` : "No email",
            patient.mobilePhoneNumber ? `Phone: ${patient.mobilePhoneNumber}` : "No phone",
          ];
          return (
            <ListItemButton
              key={id}
              selected={selected}
              onClick={() => onSelectPatient?.(patient, "patient")}
              sx={{
                borderBottom: "1px solid #eeeeee",
                alignItems: compact ? "center" : "flex-start",
                py: compact ? 1 : 1.25,
                "&.Mui-selected": { bgcolor: "rgba(80, 0, 0, 0.10)" },
                "&.Mui-selected:hover": { bgcolor: "rgba(80, 0, 0, 0.14)" },
              }}
            >
              {compact ? (
                <Stack direction="row" sx={{ width: "100%", px: 2, alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" noWrap title={name} sx={{ flex: 6, textAlign: "center", fontWeight: selected ? 700 : 600, minWidth: 0 }}>
                    {name}
                  </Typography>
                  <Typography variant="body2" noWrap title={patient.patientAccountNumber || "No MRN"} sx={{ flex: 4, textAlign: "center", color: "text.secondary", minWidth: 0 }}>
                    {patient.patientAccountNumber || "No MRN"}
                  </Typography>
                  <Typography variant="body2" noWrap title={patient.email || "No email"} sx={{ flex: 8, textAlign: "center", color: "text.secondary", minWidth: 0 }}>
                    {patient.email || "No email"}
                  </Typography>
                  {showPhone && (
                    <Typography variant="body2" noWrap title={patient.mobilePhoneNumber || "No phone"} sx={{ flex: 5, textAlign: "center", color: "text.secondary", minWidth: 0 }}>
                      {patient.mobilePhoneNumber || "No phone"}
                    </Typography>
                  )}
                </Stack>
              ) : (
                <ListItemText
                  primary={name}
                  secondary={showContact ? contactLines.join(" | ") : patient.patientAccountNumber || "No MRN"}
                  primaryTypographyProps={{
                    fontWeight: selected ? 700 : 600,
                    noWrap: true,
                    title: name,
                    variant: "body2",
                  }}
                  secondaryTypographyProps={{
                    noWrap: !showContact,
                    title: showContact ? contactLines.join(" | ") : patient.patientAccountNumber || "No MRN",
                    variant: "body2",
                  }}
                  sx={{ my: 0 }}
                />
              )}
            </ListItemButton>
          );
        })}
      </List>

      <Divider />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 1 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 88 }}>
          {from}-{to} of {totalElements}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => setPage((current) => current - 1)} disabled={!canPrevious}>
            Prev
          </Button>
          <Button size="small" onClick={() => setPage((current) => current + 1)} disabled={!canNext}>
            Next
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default EmailPatientPicker;
